import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Container,
  TextField,
  Stack,
  Grid,
  InputAdornment,
  Chip,
  Divider,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AppsRoundedIcon from "@mui/icons-material/AppsRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import { useNavigate } from "react-router-dom";
import { SistemaCard } from "../../../Components/Sistema/ListagemSistemas";
import { useUsuario } from "../../../Hooks/UsuarioHook";
import { app_base_url } from "../../../Utils/Urls";
import { podeCriarSistema } from "../../../Utils/permissoes";
import {
  agruparPorCategoria,
  metadadosCategoria,
} from "../../../Utils/categoriasSistema";
import { LiberarEquipeSinsoft } from "../../../Components/LiberarEquipeSinsoft";

export const SistemasLiberados = () => {
  const { usuario } = useUsuario();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const podeCadastrar = podeCriarSistema(usuario?.usuario);

  const primeiroNome = String(usuario?.usuario?.nome ?? "").trim().split(" ")[0];

  const sortedSistemas = useMemo(() => {
    const entidades_id = [31, 2, 6, 95, 22, 26, 24];
    const podeMostrarCidadeDigital = entidades_id.includes(usuario.entidade.id);

    const sistemasFiltrados = usuario.sistemas.filter(
      (sistema) =>
        podeMostrarCidadeDigital || sistema.titulo !== "Cidade Digital"
    );

    return sistemasFiltrados
      .slice()
      .sort((a, b) => a.titulo.localeCompare(b.titulo));
  }, [usuario.sistemas, usuario.entidade.id]);

  const filteredSistemas = useMemo(() => {
    const termo = searchTerm.trim().toLowerCase();
    if (!termo) return sortedSistemas;

    // busca também na descrição: quem não lembra o nome do sistema costuma
    // lembrar o que ele faz ("guia", "holerite", "processo")
    return sortedSistemas.filter(
      (sistema) =>
        sistema.titulo.toLowerCase().includes(termo) ||
        String(sistema.descricao ?? "").toLowerCase().includes(termo)
    );
  }, [searchTerm, sortedSistemas]);

  // Agrupa nas categorias (ver Utils/categoriasSistema.ts). Durante a busca o
  // agrupamento continua valendo — ajuda a situar o resultado.
  const grupos = useMemo(
    () => agruparPorCategoria(filteredSistemas),
    [filteredSistemas]
  );

  return (
    <Container
      maxWidth={false}
      sx={{
        maxWidth: "95vw",
        mx: "auto",
        bgcolor: "background.paper",
        px: { xs: 2, md: 3, lg: 3.5 },
        py: { xs: 1.75, md: 2.25 },
        borderRadius: 2,
        boxShadow: (t) =>
          t.palette.mode === "dark"
            ? "0 6px 18px rgba(0,0,0,0.4)"
            : "0 6px 18px rgba(0,0,0,0.08)",
        border: 1,
        borderColor: "divider",
      }}
    >
      <Stack spacing={2}>
        {/* Manutenção Sinsoft: só aparece para os CPFs da equipe. Fica na home
            porque é a primeira tela — dentro de modal ninguém achava. */}
        <LiberarEquipeSinsoft />

        {/* Cabeçalho com título, contador e busca — enxuto: saudação e contador
            na MESMA linha, para não gastar altura antes dos sistemas. */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                display: "flex",
                p: 0.85,
                borderRadius: 2,
                bgcolor: "rgba(79,142,247,0.12)",
                color: "primary.main",
              }}
            >
              <AppsRoundedIcon sx={{ fontSize: 20 }} />
            </Box>

            {/* Este é o cabeçalho REAL da tela — precisa ser o texto mais forte
                da área de conteúdo, acima do título discreto da TopBar. */}
            <Typography
              sx={{
                fontSize: "1.2rem",
                fontWeight: 800,
                lineHeight: 1.2,
                color: "text.primary",
                whiteSpace: "nowrap",
              }}
            >
              {primeiroNome ? `Olá, ${primeiroNome}` : "Sistemas Liberados"}
            </Typography>

            <Typography
              color="text.secondary"
              sx={{ fontSize: "0.78rem", whiteSpace: "nowrap" }}
            >
              {searchTerm
                ? `· ${filteredSistemas.length} de ${sortedSistemas.length}`
                : `· ${sortedSistemas.length} ${
                    sortedSistemas.length === 1 ? "sistema" : "sistemas"
                  }`}
            </Typography>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              variant="outlined"
              placeholder="Pesquisar sistemas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{
                width: { xs: "100%", md: 320 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 50,
                  bgcolor: "background.default",
                  "& fieldset": { borderColor: "divider" },
                  "&:hover fieldset": { borderColor: "primary.main" },
                  "&.Mui-focused": {
                    boxShadow: "0 0 0 4px rgba(79,142,247,0.15)",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {/* Cadastro de sistema — só para os CPFs de manutenção (mesma
                condição da rota, ver Utils/permissoes.ts) */}
            {podeCadastrar && (
              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => navigate(`${app_base_url}/criar-sistema`)}
                sx={{ borderRadius: 50, whiteSpace: "nowrap", px: 2.5 }}
              >
                Novo sistema
              </Button>
            )}
          </Stack>
        </Stack>

        {/* Sistemas agrupados por categoria */}
        {grupos.length > 0 ? (
          <Stack spacing={2.5}>
            {grupos.map(({ categoria, sistemas }) => {
              const meta = metadadosCategoria(categoria);
              const IconeCategoria = meta.icone;

              return (
                <Box key={categoria}>
                  {/* Faixa da categoria — tudo em UMA linha, para não gastar
                      altura. A descrição da categoria virou tooltip do ícone. */}
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 1.25 }}
                  >
                    <Tooltip title={meta.descricao} arrow placement="top">
                      <Box
                        sx={{
                          display: "flex",
                          p: 0.6,
                          borderRadius: 1.5,
                          color: (t) =>
                            t.palette.mode === "dark"
                              ? `hsl(${meta.hue} 70% 72%)`
                              : `hsl(${meta.hue} 55% 38%)`,
                          bgcolor: (t) =>
                            t.palette.mode === "dark"
                              ? `hsl(${meta.hue} 40% 20%)`
                              : `hsl(${meta.hue} 72% 95%)`,
                        }}
                      >
                        <IconeCategoria sx={{ fontSize: 16 }} />
                      </Box>
                    </Tooltip>

                    <Typography
                      sx={{
                        fontSize: "0.78rem",
                        fontWeight: 800,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        lineHeight: 1.2,
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {categoria}
                    </Typography>

                    <Chip
                      label={sistemas.length}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        "& .MuiChip-label": { px: 0.75 },
                        color: (t) =>
                          t.palette.mode === "dark"
                            ? `hsl(${meta.hue} 70% 78%)`
                            : `hsl(${meta.hue} 55% 33%)`,
                        bgcolor: (t) =>
                          t.palette.mode === "dark"
                            ? `hsl(${meta.hue} 40% 22%)`
                            : `hsl(${meta.hue} 72% 93%)`,
                      }}
                    />

                    {/* linha que puxa o olho até o fim da faixa */}
                    <Divider sx={{ flex: 1, ml: 0.5 }} />
                  </Stack>

                  {/* Grade de 24 colunas para caber 8 por linha em tela grande
                      (com 12 colunas não dá: 12/8 não é inteiro). Card só com
                      ícone + nome não precisa da largura de antes.
                      celular 2 · tablet 4 · notebook 6 · desktop 8 */}
                  <Grid container columns={24} spacing={1.25} justifyContent="flex-start">
                    {sistemas.map((sistema) => (
                      <Grid
                        item
                        xs={12}
                        sm={6}
                        md={4}
                        lg={3}
                        key={sistema.id}
                        display="flex"
                      >
                        <SistemaCard sistema={sistema} />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}
          </Stack>
        ) : (
          <Stack alignItems="center" spacing={1} py={6}>
            <SearchOffRoundedIcon sx={{ fontSize: 44, color: "text.disabled" }} />
            <Typography color="text.secondary" fontWeight={600}>
              Nenhum sistema encontrado
            </Typography>
            <Typography variant="body2" color="text.disabled">
              Tente buscar por outro nome ou pelo que o sistema faz.
            </Typography>
          </Stack>
        )}
      </Stack>
    </Container>
  );
};
